/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import {
  MutationKey,
  useMutation,
  UseMutationResult,
} from "@tanstack/react-query";
import { GramCore } from "../core.js";
import { externalKeysDeleteGcpKms } from "../funcs/externalKeysDeleteGcpKms.js";
import { combineSignals } from "../lib/primitives.js";
import { RequestOptions } from "../lib/sdks.js";
import { GramError } from "../models/errors/gramerror.js";
import {
  ConnectionError,
  InvalidRequestError,
  RequestAbortedError,
  RequestTimeoutError,
  UnexpectedClientError,
} from "../models/errors/httpclienterrors.js";
import { ResponseValidationError } from "../models/errors/responsevalidationerror.js";
import { SDKValidationError } from "../models/errors/sdkvalidationerror.js";
import { ServiceError } from "../models/errors/serviceerror.js";
import {
  DeleteGcpKmsKeyRequest,
  DeleteGcpKmsKeySecurity,
} from "../models/operations/deletegcpkmskey.js";
import { unwrapAsync } from "../types/fp.js";
import { useGramContext } from "./_context.js";
import { MutationHookOptions } from "./_types.js";

export type DeleteGcpKmsKeyMutationVariables = {
  request: DeleteGcpKmsKeyRequest;
  security?: DeleteGcpKmsKeySecurity | undefined;
  options?: RequestOptions;
};

export type DeleteGcpKmsKeyMutationData = void;

export type DeleteGcpKmsKeyMutationError =
  | ServiceError
  | GramError
  | ResponseValidationError
  | ConnectionError
  | RequestAbortedError
  | RequestTimeoutError
  | InvalidRequestError
  | UnexpectedClientError
  | SDKValidationError;

/**
 * deleteGcpKmsKey externalKeys
 *
 * @remarks
 * Soft-delete a GCP KMS external key by ID. Requires org:admin. Refused with a conflict while any JSON Web Key Set or published JSON Web Key still references the key, since deleting it would break verification for every already-published kid.
 */
export function useDeleteGcpKmsKeyMutation(
  options?: MutationHookOptions<
    DeleteGcpKmsKeyMutationData,
    DeleteGcpKmsKeyMutationError,
    DeleteGcpKmsKeyMutationVariables
  >,
): UseMutationResult<
  DeleteGcpKmsKeyMutationData,
  DeleteGcpKmsKeyMutationError,
  DeleteGcpKmsKeyMutationVariables
> {
  const client = useGramContext();
  return useMutation({
    ...buildDeleteGcpKmsKeyMutation(client, options),
    ...options,
  });
}

export function mutationKeyDeleteGcpKmsKey(): MutationKey {
  return ["@gram/client", "externalKeys", "deleteGcpKms"];
}

export function buildDeleteGcpKmsKeyMutation(
  client$: GramCore,
  hookOptions?: RequestOptions,
): {
  mutationKey: MutationKey;
  mutationFn: (
    variables: DeleteGcpKmsKeyMutationVariables,
  ) => Promise<DeleteGcpKmsKeyMutationData>;
} {
  return {
    mutationKey: mutationKeyDeleteGcpKmsKey(),
    mutationFn: function deleteGcpKmsKeyMutationFn({
      request,
      security,
      options,
    }): Promise<DeleteGcpKmsKeyMutationData> {
      const mergedOptions = {
        ...hookOptions,
        ...options,
        fetchOptions: {
          ...hookOptions?.fetchOptions,
          ...options?.fetchOptions,
          signal: combineSignals(
            hookOptions?.fetchOptions?.signal,
            options?.fetchOptions?.signal,
          ),
        },
      };
      return unwrapAsync(externalKeysDeleteGcpKms(
        client$,
        request,
        security,
        mergedOptions,
      ));
    },
  };
}
